import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import { TableCellType } from "components/LazyTable";
import Loader from "components/Loader";
import SortableTable from "components/SortableTable";
import useBoolean from "hooks/useBoolean";
import useGlobalContext from "hooks/globalData";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import {
  IRequirementApplicabilityConditionDetail,
  IRequirementAuthoringOptions,
  IRequirementConstraintDetail,
} from "services/KnowfullyAdminClient";
import translateError from "utils/translateError";
import ConditionDialog from "./ConditionDialog";
import ConstraintDialog from "./ConstraintDialog";
import ensureOk from "./ensureOk";
import NotesSection from "./NotesSection";
import RequirementDialog from "./RequirementDialog";
import RowActionsCell from "./RowActionsCell";
import styles from "./styles";

type RequirementPaneProps = {
  requirementId: string;
  requirementSetId: string;
  options?: IRequirementAuthoringOptions;
  locked: boolean;
  hasPrevious: boolean;
  hasNext: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onChanged: () => void;
  onDeleted: () => void;
  /** The page owns the snackbar, so a refusal from one of these actions is reported through it. */
  onError: (message: string) => void;
};

const RequirementPane = ({
  requirementId,
  requirementSetId,
  options,
  locked,
  hasPrevious,
  hasNext,
  onPrevious,
  onNext,
  onChanged,
  onDeleted,
  onError,
}: RequirementPaneProps) => {
  const { formatMessage: t } = useIntl();
  const { requirementService } = useGlobalContext();

  const [editingRequirement, editRequirement] = useBoolean(false);
  const [deletingRequirement, deleteRequirement] = useBoolean(false);
  const [editingConstraint, setEditingConstraint] = useState<{
    constraint?: IRequirementConstraintDetail;
  }>();
  const [editingCondition, setEditingCondition] = useState<{
    condition?: IRequirementApplicabilityConditionDetail;
  }>();
  const [deletingConstraint, setDeletingConstraint] = useState<IRequirementConstraintDetail>();
  const [deletingCondition, setDeletingCondition] =
    useState<IRequirementApplicabilityConditionDetail>();

  const requirementResult = useQuery(
    ["requirementService.getRequirementById", requirementId],
    () => requirementService.getRequirementById(requirementId)
  );

  const requirement = requirementResult.data?.data;

  const reload = () => {
    requirementResult.refetch();
    onChanged();
  };

  if (requirementResult.isLoading) return <Loader />;
  if (!requirement) {
    return <Alert severity="info">{t({ id: "requirementNotFound" })}</Alert>;
  }

  const constraints = requirement.constraints ?? [];
  const conditions = requirement.applicabilityConditions ?? [];

  /**
   * A refused rule comes back as HTTP 200 with a ResponseCode, so every one of these has to be run
   * through ensureOk or the screen would silently pretend it worked.
   */
  const run = async (action: () => Promise<unknown>, onDone: () => void) => {
    try {
      ensureOk((await action()) as Parameters<typeof ensureOk>[0]);
      onDone();
    } catch (error) {
      onError(translateError(error, t));
    }
  };

  const move = (moveUp: boolean) =>
    run(
      () =>
        moveUp
          ? requirementService.moveRequirementUp(requirementId)
          : requirementService.moveRequirementDown(requirementId),
      onChanged
    );

  /** The one typed value a constraint carries, whichever kind it is. */
  const constraintValue = (constraint: IRequirementConstraintDetail) =>
    constraint.topicCategoryName ??
    constraint.formatTypeName ??
    constraint.accreditorName ??
    constraint.audienceDesignator ??
    "—";

  const rowActions = (onEdit: () => void, onDelete: () => void) =>
    locked ? {} : { onEdit, onDelete };

  const constraintCells: Array<TableCellType> = [
    {
      accessor: "constraintTypeName",
      type: "text",
      label: t({ id: "constraintType" }),
    },
    {
      accessor: "value",
      type: "text",
      label: t({ id: "valueLabel" }),
    },
    {
      accessor: "logicalGroup",
      type: "text",
      label: t({ id: "logicalGroup" }),
    },
    {
      accessor: "actions",
      align: "right",
      type: "node",
      noSort: true,
      label: t({ id: "actions" }),
      View: RowActionsCell,
    },
  ];

  const constraintRows = constraints.map((constraint) => ({
    ...constraint,
    id: constraint.id ?? "",
    value: constraintValue(constraint),
    ...rowActions(
      () => setEditingConstraint({ constraint }),
      () => setDeletingConstraint(constraint)
    ),
  }));

  const conditionCells: Array<TableCellType> = [
    {
      accessor: "conditionTypeName",
      type: "text",
      label: t({ id: "conditionType" }),
    },
    {
      accessor: "operatorTypeName",
      type: "text",
      label: t({ id: "operatorLabel" }),
    },
    {
      accessor: "referenceValue",
      type: "text",
      label: t({ id: "referenceValue" }),
    },
    {
      accessor: "rangeEnd",
      type: "text",
      label: t({ id: "rangeEndValue" }),
    },
    {
      accessor: "actions",
      align: "right",
      type: "node",
      noSort: true,
      label: t({ id: "actions" }),
      View: RowActionsCell,
    },
  ];

  const conditionRows = conditions.map((condition) => ({
    ...condition,
    id: condition.id ?? "",
    rangeEnd: condition.rangeEndValue ?? "—",
    ...rowActions(
      () => setEditingCondition({ condition }),
      () => setDeletingCondition(condition)
    ),
  }));

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box sx={styles.paneHead}>
        <Box display="flex" alignItems="center" gap={1}>
          <Chip size="small" label={requirement.sortOrder} />
          <Typography variant="h6">{requirement.name}</Typography>
          <Chip size="small" label={requirement.ruleTypeName} />
        </Box>

        <Box display="flex" gap={0.5}>
          {/* Stepping between requirements is reading, so it survives the lock. */}
          <Tooltip title={t({ id: "previousRequirement" })}>
            <IconButton size="small" disabled={!hasPrevious} onClick={onPrevious}>
              <KeyboardArrowUpIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t({ id: "nextRequirement" })}>
            <IconButton size="small" disabled={!hasNext} onClick={onNext}>
              <KeyboardArrowDownIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {!locked && (
            <>
              <Tooltip title={t({ id: "moveUp" })}>
                <IconButton size="small" onClick={() => move(true)}>
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t({ id: "moveDown" })}>
                <IconButton size="small" onClick={() => move(false)}>
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t({ id: "editLabel" })}>
                <IconButton size="small" onClick={editRequirement.setTrue}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t({ id: "deleteRequirement" })}>
                <IconButton size="small" onClick={deleteRequirement.setTrue}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell width="30%">
                <b>{t({ id: "measureType" })}</b>
              </TableCell>
              <TableCell>{requirement.measureTypeName}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <b>{t({ id: "amountLabel" })}</b>
              </TableCell>
              <TableCell>{requirement.amount}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <b>{t({ id: "recurrenceType" })}</b>
              </TableCell>
              <TableCell>
                {requirement.recurrenceTypeName}
                {requirement.recurrenceIntervalCycles
                  ? ` · ${requirement.recurrenceIntervalCycles}`
                  : ""}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>
                <b>{t({ id: "descriptionLabel" })}</b>
              </TableCell>
              <TableCell>{requirement.description ?? "—"}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Accordion sx={{ mt: 2 }} defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>
            {t({ id: "requirementConstraints" })} ({constraints.length})
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={styles.sectionActions}>
            <Box />
            {!locked && (
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setEditingConstraint({})}
              >
                {t({ id: "addConstraint" })}
              </Button>
            )}
          </Box>

          {constraints.length === 0 ? (
            <Alert severity="info">{t({ id: "noConstraints" })}</Alert>
          ) : (
            <SortableTable
              cells={constraintCells}
              rows={constraintRows}
              dense
              defaultOrderBy="logicalGroup"
            />
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion sx={{ mt: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>
            {t({ id: "applicabilityConditions" })} ({conditions.length})
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={styles.sectionActions}>
            <Box />
            {!locked && (
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setEditingCondition({})}
              >
                {t({ id: "addCondition" })}
              </Button>
            )}
          </Box>

          {conditions.length === 0 ? (
            <Alert severity="info">{t({ id: "noApplicabilityConditions" })}</Alert>
          ) : (
            <SortableTable
              cells={conditionCells}
              rows={conditionRows}
              dense
              defaultOrderBy="conditionTypeName"
            />
          )}
        </AccordionDetails>
      </Accordion>

      <NotesSection
        requirementSetId={requirementSetId}
        requirementId={requirementId}
        notes={requirement.notes ?? []}
        options={options}
        locked={locked}
        onChanged={reload}
        onError={onError}
      />

      {editingRequirement && (
        <RequirementDialog
          requirementSetId={requirementSetId}
          requirement={requirement}
          options={options}
          open
          onClose={editRequirement.setFalse}
          onSaved={() => {
            editRequirement.setFalse();
            reload();
          }}
        />
      )}

      {editingConstraint && (
        <ConstraintDialog
          requirementId={requirementId}
          constraint={editingConstraint.constraint}
          options={options}
          open
          onClose={() => setEditingConstraint(undefined)}
          onSaved={() => {
            setEditingConstraint(undefined);
            reload();
          }}
        />
      )}

      {editingCondition && (
        <ConditionDialog
          requirementId={requirementId}
          condition={editingCondition.condition}
          options={options}
          open
          onClose={() => setEditingCondition(undefined)}
          onSaved={() => {
            setEditingCondition(undefined);
            reload();
          }}
        />
      )}

      <DeleteEntityDialog
        open={deletingRequirement}
        dialogTitleKey="deleteRequirementQuestion"
        rawContent={t(
          { id: "deleteRequirementWarning" },
          { name: requirement.name ?? "" }
        )}
        onCancel={deleteRequirement.setFalse}
        handleDelete={() =>
          run(() => requirementService.deleteRequirement(requirementId), () => {
            deleteRequirement.setFalse();
            onDeleted();
          })
        }
      />

      <DeleteEntityDialog
        open={!!deletingConstraint}
        dialogTitleKey="deleteConstraintQuestion"
        contentKey="deleteConstraintWarning"
        onCancel={() => setDeletingConstraint(undefined)}
        handleDelete={() =>
          run(
            () =>
              requirementService.deleteRequirementConstraint(
                deletingConstraint!.id!
              ),
            () => {
              setDeletingConstraint(undefined);
              reload();
            }
          )
        }
      />

      <DeleteEntityDialog
        open={!!deletingCondition}
        dialogTitleKey="deleteApplicabilityConditionQuestion"
        contentKey="deleteApplicabilityConditionWarning"
        onCancel={() => setDeletingCondition(undefined)}
        handleDelete={() =>
          run(
            () =>
              requirementService.deleteApplicabilityCondition(
                deletingCondition!.id!
              ),
            () => {
              setDeletingCondition(undefined);
              reload();
            }
          )
        }
      />
    </Paper>
  );
};

export default RequirementPane;
